import type { User } from '@repo-level/core-front';
import { sendGAEvent } from '../utils';

export enum AuthEvents {
  LOGIN_FORM_APPEARANCE = 'login_form_appearance',
  LOGIN_GOOGLE_AUTH_BUTTON_CLICK = 'login_google_auth_button_click',
  LOGIN_FORM_SUBMIT = 'login_form_submit',
  LOGIN_FORM_CLOSE = 'login_form_close',
  REGISTRATION_FORM_APPEARANCE = 'registration_form_appearance',
  REGISTRATION_GOOGLE_AUTH_BUTTON_CLICK = 'registration_google_auth_button_click',
  REGISTRATION_FORM_SUBMIT = 'registration_form_submit',
  REGISTRATION_FORM_CLOSE = 'registration_form_close',
}

const LOGIN_CATEGORY = 'login';
const REGISTRATION_CATEGORY = 'registration';

export type TAuthFormPlace =
  | 'modal-lobby'
  | 'modal-landing'
  | 'landing'
  | 'lobby';

export type TAuthMethod = 'email' | 'google';

export type TAuthStatus = 'success' | 'failed';

type TAuthenticatedPlayer = Pick<User, 'email' | 'currency' | 'country'>;

interface ILoginFormSubmitEvent {
  status: TAuthStatus;
  method: TAuthMethod;
  user?: TAuthenticatedPlayer;
  error_data?: unknown;
}

interface IRegistrationFormSubmitEvent {
  status: TAuthStatus;
  method: TAuthMethod;
  type: TAuthFormPlace;
  action: 'login' | 'registration';
  error_data?: unknown;
}

/**
 * @description Appearance of the login form or its welcome back version
 * (`?modal=login` URL).
 */
export const sendLoginFormAppearanceEvent = () => {
  sendGAEvent(AuthEvents.LOGIN_FORM_APPEARANCE, {
    event_category: LOGIN_CATEGORY,
  });
};

/**
 * @description Click on the google auth button of the login form.
 */
export const sendLoginGoogleAuthButtonClickEvent = () => {
  sendGAEvent(AuthEvents.LOGIN_GOOGLE_AUTH_BUTTON_CLICK, {
    event_category: LOGIN_CATEGORY,
  });
};

/**
 * @description The result of a login, sent after the form submission or after
 * an authentication started by the google auth button.
 */
export const sendLoginFormSubmitEvent = ({
  user,
  ...data
}: ILoginFormSubmitEvent) => {
  sendGAEvent(AuthEvents.LOGIN_FORM_SUBMIT, {
    event_category: LOGIN_CATEGORY,
    ...data,
    ...(user
      ? {
          request_data: JSON.stringify({
            email: user.email,
            currency: user.currency,
            country: user.country,
          }),
        }
      : {}),
  });
};

/**
 * @description Click on the cross icon of the login form.
 */
export const sendLoginFormCloseEvent = () => {
  sendGAEvent(AuthEvents.LOGIN_FORM_CLOSE, {
    event_category: LOGIN_CATEGORY,
  });
};

/**
 * @description Appearance of the registration form.
 */
export const sendRegistrationFormAppearanceEvent = (type: TAuthFormPlace) => {
  sendGAEvent(AuthEvents.REGISTRATION_FORM_APPEARANCE, {
    event_category: REGISTRATION_CATEGORY,
    type,
  });
};

/**
 * @description Click on the google auth button of the registration form.
 */
export const sendRegistrationGoogleAuthButtonClickEvent = (
  type: TAuthFormPlace
) => {
  sendGAEvent(AuthEvents.REGISTRATION_GOOGLE_AUTH_BUTTON_CLICK, {
    event_category: REGISTRATION_CATEGORY,
    type,
  });
};

/**
 * @description The result of a registration attempt. An existing player who
 * submits valid credentials is logged in instead, hence the action attribute.
 */
export const sendRegistrationFormSubmitEvent = (
  data: IRegistrationFormSubmitEvent
) => {
  sendGAEvent(AuthEvents.REGISTRATION_FORM_SUBMIT, {
    event_category: REGISTRATION_CATEGORY,
    ...data,
  });
};

/**
 * @description Click on the cross icon of the registration form.
 */
export const sendRegistrationFormCloseEvent = (type: TAuthFormPlace) => {
  sendGAEvent(AuthEvents.REGISTRATION_FORM_CLOSE, {
    event_category: REGISTRATION_CATEGORY,
    type,
  });
};
